import * as React from 'react'
import { theme } from '../theme'
import { UndoIcon, CheckIcon } from '../icons'

interface Props {
	// Common props
	isDisabled?: boolean // For backward compatibility
	isReturnDisabled?: boolean
	isAcceptDisabled?: boolean
	onReturn?: () => void
	onAccept?: () => void
	// Optional customization
	returnLabel?: string
	acceptLabel?: string
	returnIcon?: React.ReactNode
	acceptIcon?: React.ReactNode
	// Style variants
	variant?: 'compact' | 'full' // compact for tool buttons, full for larger buttons
}

export const ActionButtons = ({
	isDisabled,
	isReturnDisabled,
	isAcceptDisabled,
	onReturn,
	onAccept,
	returnLabel = '',
	acceptLabel = '',
	returnIcon = (
		<UndoIcon size={16} color={isDisabled ?? isReturnDisabled ? theme.colors.text.muted : theme.colors.error} />
	),
	acceptIcon = (
		<CheckIcon size={16} color={isDisabled ?? isAcceptDisabled ? theme.colors.text.muted : theme.colors.surface} />
	),
	variant = 'compact',
}: Props): JSX.Element => {
	// For backward compatibility, if isDisabled is set, use it for both buttons
	const returnDisabled = isDisabled ?? isReturnDisabled ?? false
	const acceptDisabled = isDisabled ?? isAcceptDisabled ?? false
	const [isAcceptHovered, setIsAcceptHovered] = React.useState(false)

	const toolButtonStyle = {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing.sm,
		color: theme.colors.text.muted,
		fontSize: variant === 'compact' ? theme.typography.sizes.tiny : theme.typography.sizes.sm,
		lineHeight: theme.typography.lineHeight.compact,
		fontFamily: theme.typography.fontFamily,
		cursor: 'pointer',
		padding: variant === 'compact' ? theme.spacing.sm : theme.spacing.md,
		borderRadius: theme.borderRadius.sm,
		transition: 'all 0.2s ease',
		backgroundColor: 'transparent',
	}

	return (
		<div style={{ display: 'flex', gap: theme.spacing.sm }}>
			{/* Return button - always visible */}
			<div
				style={{
					...toolButtonStyle,
					cursor: returnDisabled ? 'not-allowed' : 'pointer',
					opacity: returnDisabled ? 0.5 : 1,
				}}
				onClick={returnDisabled ? undefined : onReturn}
				title="Return"
			>
				{returnIcon}
				{variant === 'full' && returnLabel && <span>{returnLabel}</span>}
			</div>

			{/* Accept button - always visible */}
			<div
				style={{
					...toolButtonStyle,
					cursor: acceptDisabled ? 'not-allowed' : 'pointer',
					opacity: acceptDisabled ? 0.5 : 1,
					backgroundColor: acceptDisabled
						? 'transparent'
						: isAcceptHovered
						? 'rgba(47, 123, 118, 0.9)'
						: '#2F7B76',
					color: acceptDisabled ? theme.colors.text.muted : theme.colors.surface,
					minWidth: acceptLabel ? '120px' : 'auto',
					justifyContent: 'center',
				}}
				onClick={acceptDisabled ? undefined : onAccept}
				onMouseEnter={() => setIsAcceptHovered(true)}
				onMouseLeave={() => setIsAcceptHovered(false)}
				title={acceptLabel || 'Accept'}
			>
				{acceptIcon}
				{acceptLabel && <span>{acceptLabel}</span>}
			</div>
		</div>
	)
}
